import { apiClient } from "@/lib/api";
import { buildOrderTemplateFilterParams } from "@/lib/orderTemplateFilters";
import type {
  BuildRouteFromTemplatesPayload,
  CreateOrderTemplatePayload,
  ImportOrderTemplatesResult,
  OrderTemplate,
  OrderTemplateFilters,
  OrderTemplateImportBatchesResponse,
  OrderTemplateRouteTemplateUsageResponse,
  OrderTemplateTagsResponse,
  PaginatedOrderTemplates,
  UpdateOrderTemplatePayload,
} from "@/types/orderTemplates";
import type { BuildRouteTaskResponse } from "@/types/routes";

export interface GetOrderTemplatesParams {
  page?: number;
  search?: string;
  /**
   * Rows per page. **Capped at 200 by the server**
   * (`apps/core/pagination.py`, `max_page_size = 200`) — probed live:
   * `?page_size=2000` returns 200, silently. The default is 20.
   *
   * That cap is why a picker over this list must page or search rather than
   * "fetch everything": a tenant with 1229 permanent orders needs 7 requests to
   * enumerate them, and a single unpaged request shows 20 of 1229 with nothing
   * on screen to say so — which is BUG-557 in a new form.
   */
  page_size?: number;
  /**
   * Server-side filters (Sprint 200). Every field is optional and anything
   * empty is omitted from the request — see
   * {@link buildOrderTemplateFilterParams}. Filtering is evaluated on the
   * server so the paginated `count` is the TRUE size of the filtered set; that
   * number is what the picker's select-all control prints on itself, and a
   * count derived from partially-loaded pages cannot be trusted.
   */
  filters?: OrderTemplateFilters;
}

/**
 * Fetches a paginated list of order templates for the current tenant.
 * GET /api/v1/order-templates/
 */
export async function getOrderTemplates(
  params?: GetOrderTemplatesParams,
): Promise<PaginatedOrderTemplates> {
  const { filters, ...rest } = params ?? {};
  const response = await apiClient.get<PaginatedOrderTemplates>(
    "/v1/order-templates/",
    {
      params: { ...rest, ...buildOrderTemplateFilterParams(filters) },
      // Redundant with the global `apiClient` serializer, kept explicit because
      // THIS call is the one with a repeatable multi-value param: `tags` must
      // reach the wire as `?tags=a&tags=b`. axios's default bracketed form
      // (`?tags[]=a`) is invisible to Django's `query_params.getlist("tags")`,
      // so the filter would silently return every row.
      paramsSerializer: { indexes: null },
    },
  );
  return response.data;
}

/**
 * Distinct tags on the tenant's permanent orders, with per-tag counts.
 * GET /api/v1/order-templates/tags/
 *
 * Ordered by count descending then tag ascending. **Returns
 * `{results: []}` on every tenant today** — no order template carries a tag
 * yet — and that is a 200, not a failure: the caller must distinguish "this
 * tenant has no tags" from "the request failed".
 */
export async function getOrderTemplateTags(params?: {
  include_paused?: boolean;
}): Promise<OrderTemplateTagsResponse> {
  const response = await apiClient.get<OrderTemplateTagsResponse>(
    "/v1/order-templates/tags/",
    params?.include_paused ? { params: { include_paused: 1 } } : undefined,
  );
  return response.data;
}

/**
 * Import batches — the creation DATES permanent orders were imported on.
 * GET /api/v1/order-templates/import_batches/
 *
 * Grouped by creation date in the tenant's timezone, NOT by an import batch id
 * (`OrderTemplate` has none — that column exists only on `Order`). Newest first,
 * capped at 30 days; `truncated` says whether the cap bit.
 */
export async function getOrderTemplateImportBatches(params?: {
  include_paused?: boolean;
}): Promise<OrderTemplateImportBatchesResponse> {
  const response = await apiClient.get<OrderTemplateImportBatchesResponse>(
    "/v1/order-templates/import_batches/",
    params?.include_paused ? { params: { include_paused: 1 } } : undefined,
  );
  return response.data;
}

/**
 * Fetches a single order template by ID.
 * GET /api/v1/order-templates/{id}/
 */
export async function getOrderTemplate(id: number): Promise<OrderTemplate> {
  const response = await apiClient.get<OrderTemplate>(
    `/v1/order-templates/${id}/`,
  );
  return response.data;
}

/**
 * Creates a new order template.
 * POST /api/v1/order-templates/
 */
export async function createOrderTemplate(
  payload: CreateOrderTemplatePayload,
): Promise<OrderTemplate> {
  const response = await apiClient.post<OrderTemplate>(
    "/v1/order-templates/",
    payload,
  );
  return response.data;
}

/**
 * Partially updates an existing order template.
 * PATCH /api/v1/order-templates/{id}/
 */
export async function updateOrderTemplate(
  id: number,
  payload: UpdateOrderTemplatePayload,
): Promise<OrderTemplate> {
  const response = await apiClient.patch<OrderTemplate>(
    `/v1/order-templates/${id}/`,
    payload,
  );
  return response.data;
}

/**
 * Deletes an order template by ID.
 * DELETE /api/v1/order-templates/{id}/
 *
 * **This is a HARD delete** (unlike `DELETE /v1/orders/{id}/`, which archives),
 * and `RouteTemplateStop.order_template` is `CASCADE` — so deleting a card
 * removes it from every route template's stop list. The server refuses the first
 * attempt with 409 `ORDER_TEMPLATE_IN_ROUTE_TEMPLATE` when a route template's
 * CURRENT stop list would be emptied, carrying the blocking templates in
 * `extra_data.route_templates` (TASK-5057, tracker#17: Dobronom lost five route
 * templates to exactly this, 2026-10-02).
 *
 * Pass `confirm: true` to go through — the policy is warn-and-confirm, not a
 * prohibition: the delete-and-re-import workflow is preserved, the consequence is
 * only made visible first.
 */
export async function deleteOrderTemplate(
  id: number,
  options?: { confirm?: boolean },
): Promise<void> {
  await apiClient.delete(`/v1/order-templates/${id}/`, {
    // Only sent when confirming: the server's whitelist treats `confirm=false`
    // as not-confirmed anyway, but omitting it keeps the unconfirmed request
    // identical to what every other caller sends.
    params: options?.confirm ? { confirm: 1 } : undefined,
  });
}

/**
 * Which route templates use these permanent orders — the delete pre-flight.
 * GET /api/v1/order-templates/route_template_usage/?ids=1&ids=2
 *
 * **ONE request for a whole selection** (TASK-5057). The bulk delete on the
 * Orders page is N independent `DELETE` calls, and it is the path that caused the
 * Dobronom incident; a pre-flight that were also N calls would be skipped, so this
 * answers the entire selection at once and the dialog can say «3 из 12 выбранных
 * заказов используются в шаблонах: …».
 *
 * At most 200 ids (the list's own page ceiling); more is a 400.
 *
 * Returns an entry ONLY for ids something actually uses, so `results.length` is
 * the number of cards at risk — never the number asked about.
 */
export async function getOrderTemplateRouteTemplateUsage(
  ids: number[],
): Promise<OrderTemplateRouteTemplateUsageResponse> {
  const response = await apiClient.get<OrderTemplateRouteTemplateUsageResponse>(
    "/v1/order-templates/route_template_usage/",
    {
      params: { ids },
      // `ids` is repeatable: it must reach the wire as `?ids=1&ids=2`. axios's
      // default bracketed form (`?ids[]=1`) is invisible to Django's
      // `query_params.getlist("ids")`, which would make the pre-flight answer
      // "nothing at risk" for every selection — the exact silence this task
      // exists to remove.
      paramsSerializer: { indexes: null },
    },
  );
  return response.data;
}

/**
 * Materializes the selected permanent orders into real one-time Orders for the
 * given date and starts an async route build.
 * POST /api/v1/order-templates/build_route/ → HTTP 202
 *
 * Returns a task acknowledgement with task_id and status="pending" — the same
 * shape as POST /v1/routes/build/, so the shared build poller
 * (getBuildRouteResult) drives the progress banner unchanged.
 */
export async function buildRouteFromTemplates(
  payload: BuildRouteFromTemplatesPayload,
): Promise<BuildRouteTaskResponse> {
  const response = await apiClient.post<BuildRouteTaskResponse>(
    "/v1/order-templates/build_route/",
    payload,
  );
  return response.data;
}

/**
 * Bulk-imports permanent orders (order templates) from a CSV or XLSX file.
 * POST /api/v1/order-templates/import/
 * Content-Type is set automatically by axios when using FormData.
 * Only `delivery_address` is required; rows without coords keep null lat/lon
 * (templates are not geocoded on import).
 */
export async function importOrderTemplates(
  file: File,
): Promise<ImportOrderTemplatesResult> {
  const form = new FormData();
  form.append("file", file);
  const response = await apiClient.post<ImportOrderTemplatesResult>(
    "/v1/order-templates/import/",
    form,
  );
  return response.data;
}

/**
 * Exports permanent orders as a file (CSV or XLSX), respecting the current
 * search filter (tenant-scoped server-side).
 * GET /api/v1/order-templates/export/
 */
export async function exportOrderTemplates(params: {
  format: "xlsx" | "csv";
  search?: string;
}): Promise<Blob> {
  const response = await apiClient.get("/v1/order-templates/export/", {
    params,
    responseType: "blob",
  });
  return response.data as Blob;
}

/**
 * Downloads the permanent-orders bulk import template file.
 * GET /api/v1/order-templates/import/template/
 */
export async function downloadOrderTemplateImportTemplate(): Promise<Blob> {
  const response = await apiClient.get("/v1/order-templates/import/template/", {
    responseType: "blob",
  });
  return response.data as Blob;
}

/**
 * Saves an existing order as a template.
 * POST /api/v1/orders/{orderId}/save_as_template/
 */
export async function saveOrderAsTemplate(
  orderId: number,
  name?: string,
): Promise<OrderTemplate> {
  const body = name !== undefined ? { name } : {};
  const response = await apiClient.post<OrderTemplate>(
    `/v1/orders/${orderId}/save_as_template/`,
    body,
  );
  return response.data;
}
